
'use client';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const ToastContext = createContext({ push: () => {} });

function variantClasses(variant) {
  switch (variant) {
    case 'success':
      return 'bg-emerald-50 text-emerald-950 border-emerald-200';
    case 'error':
      return 'bg-rose-50 text-rose-950 border-rose-200';
    case 'warning':
      return 'bg-amber-50 text-amber-950 border-amber-200';
    default:
      return 'bg-slate-50 text-slate-900 border-slate-200';
  }
}

function icon(variant) {
  switch (variant) {
    case 'success':
      return '✅';
    case 'error':
      return '⛔';
    case 'warning':
      return '⚠️';
    default:
      return 'ℹ️';
  }
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const remove = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    const timeoutId = timers.current.get(id);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timers.current.delete(id);
    }
  }, []);

  const scheduleAutoDismiss = useCallback((id, duration) => {
    const tid = window.setTimeout(() => remove(id), duration);
    timers.current.set(id, tid);
  }, [remove]);

  const push = useCallback((opt) => {
    const options = typeof opt === 'string' ? { title: opt } : opt;
    const id = options.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const item = {
      id,
      title: options.title || 'Notice',
      description: options.description,
      variant: options.variant || 'info',
      duration: options.duration || 4000,
    };
    setToasts((t) => [item, ...t].slice(0, 5));
    scheduleAutoDismiss(id, item.duration);
  }, [scheduleAutoDismiss]);

  useEffect(() => {
    const w = window;
    if (!w.__toast_emitter) w.__toast_emitter = new EventTarget();
    const handler = (e) => {
      const detail = e.detail;
      push(detail);
    };
    w.__toast_emitter.addEventListener('toast:show', handler);
    return () => w.__toast_emitter.removeEventListener('toast:show', handler);
  }, [push]);

  const ctx = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div className="pointer-events-none fixed inset-0 z-50 flex flex-col items-end gap-2 p-4 sm:p-6">
        <div className="ml-auto flex w-full max-w-sm flex-col gap-2">
          {toasts.map((t) => (
            <ToastItemView key={t.id} item={t} onClose={() => remove(t.id)} />)
          )}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

function ToastItemView({ item, onClose }) {
  const [hover, setHover] = useState(false);

  return (
    <div
      role="status"
      aria-live="polite"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`pointer-events-auto relative overflow-hidden rounded-2xl border shadow-md transition-all duration-200 animate-in fade-in slide-in-from-top-2 ${variantClasses(item.variant)}`}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="text-lg leading-none">{icon(item.variant)}</div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{item.title}</p>
          {item.description && (
            <p className="mt-0.5 line-clamp-3 text-sm opacity-80">{item.description}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-black/60 transition hover:bg-black/5 hover:text-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
      <ToastProgress duration={item.duration} running={!hover} />
    </div>
  );
}

function ToastProgress({ duration, running }) {
  return (
    <div
      className="absolute bottom-0 left-0 h-1 w-full bg-black/5"
      aria-hidden
    >
      <div
        className={`h-1 w-full origin-left bg-black/20 ${running ? 'animate-[shrink_var(--d)_linear_forwards]' : ''}`}
        style={{
          ['--d']: `${duration}ms`,
          animationPlayState: running ? 'running' : 'paused',
        }}
      />
    </div>
  );
}
